import React, { useEffect, useState } from 'react';
import { Loader2, Plus, Trash2, Wallet, AlertTriangle, RefreshCw, CalendarCheck } from 'lucide-react';
import { getHourBalance, deleteHourBalance } from '../services/api';
import {
  formatBalance, balanceColor, eligibleForBalance, formatNoScheduleBalance, NO_SCHEDULE_ADMIN_HINT,
  isSupersededHourlyOffset, isComputationStartOffset, COMPUTATION_START_LABEL,
} from '../lib/hourBalance';
import { computeBalanceTotals, invalidateWorkerHourBalance } from '../hooks/useWorkerHourBalance';
import HourBalanceModal from './HourBalanceModal';
import { useToast } from './Toast';
import { FEEDBACK } from '../services/feedbackMessages';
import { timeZoneForWorker, isDayKey } from '../lib/workerTime';

// Una clave de día ('YYYY-MM-DD', p.ej. byDay del cómputo) se formatea en
// UTC para que el navegador no la desplace al día anterior.
const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    if (isDayKey(String(iso))) {
      return new Date(`${iso}T00:00:00Z`).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' });
    }
    return new Date(iso).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return iso; }
};

const fmtMinutes = (mins) => {
  const m = Math.round(Number(mins) || 0);
  const sign = m < 0 ? '−' : '';
  const abs = Math.abs(m);
  const hh = Math.floor(abs / 60);
  const mm = abs % 60;
  if (hh === 0) return `${sign}${mm}m`;
  if (mm === 0) return `${sign}${hh}h`;
  return `${sign}${hh}h ${String(mm).padStart(2, '0')}m`;
};

const SUPERSEDED_OFFSET_TOOLTIP = 'Ajuste antiguo de una ausencia por horas recuperable. Ya no se suma: desde el 22/09/2026 la bolsa recupera esas horas en el cómputo automático (la jornada del día no se reduce).';

// Componente principal de la bolsa de horas. Muestra:
//   1. Total agregado (auto + manual) destacado.
//   2. Breakdown auto / manual.
//   3. Lista de ajustes manuales con borrar (solo admin con scope).
//   4. Botón para añadir ajuste manual.
//   5. Detalle de los días que componen el cómputo automático (collapsible).
//
// Props:
//   worker     — el trabajador completo (necesita overtimeAllowed,
//                enforceStartHour para validar elegibilidad).
//   events     — eventos del trabajador (los que ya tiene cargados el padre).
//   workhours  — slots de jornada del trabajador.
//   canManage  — si es true, muestra el botón de añadir ajuste y los
//                botones de borrar. Solo admins con scope.
//   eventsError — error de carga de `events` del padre (si lo hay). Sin
//                eventos el total sería falso: se muestra error, igual que
//                cuando fallan los ajustes (y que la app).
//   refreshKey — al cambiar, la tarjeta recarga los ajustes. Lo usa la ficha
//                del trabajador tras editar la fecha de inicio de cómputo.
//
// FECHA DE INICIO DE CÓMPUTO: viene en los propios ajustes, como fila
// marcadora con offset 0 (ver src/lib/hourBalance.js). NO es un ajuste: se
// excluye de la lista de "Ajustes manuales" y del total (computeBalanceTotals
// ya lo hace) y se pinta como fila informativa.
const HourBalanceCard = ({ worker, events, workhours, holidaysSet = null, canManage = false, eventsError = null, refreshKey = 0 }) => {
  const [manualOffsets, setManualOffsets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [showAutoBreakdown, setShowAutoBreakdown] = useState(false);
  const toast = useToast();

  const eligible = eligibleForBalance(worker);

  const reload = async () => {
    if (!worker?.id || !eligible) {
      setLoading(false);
      setManualOffsets([]);
      return;
    }
    setLoading(true);
    setError(null);
    // Recargar (o volver tras añadir un ajuste) invalida el total cacheado
    // del chip de Equipo para este trabajador.
    invalidateWorkerHourBalance(worker.id);
    try {
      const list = await getHourBalance(worker.id);
      setManualOffsets(Array.isArray(list) ? list : []);
    } catch (err) {
      setError(err);
      setManualOffsets([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, [worker?.id, eligible, refreshKey]);

  const handleDelete = async (offsetId) => {
    if (!offsetId) return;
    if (!window.confirm('¿Eliminar este ajuste manual? Esta acción es irreversible.')) return;
    setDeletingId(offsetId);
    try {
      await deleteHourBalance(offsetId);
      setManualOffsets((prev) => prev.filter((o) => o.id !== offsetId));
      invalidateWorkerHourBalance(worker?.id);
      toast.success(FEEDBACK.hourBalanceDeleted);
    } catch (err) {
      toast.error(err, 'No se pudo eliminar el ajuste.');
    } finally {
      setDeletingId(null);
    }
  };

  // Si el trabajador no califica (no tiene overtimeAllowed ni puede picar
  // antes de hora), no tiene sentido mostrar la bolsa: lo decimos y fuera.
  if (!eligible) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-ink-muted font-semibold mb-2">
          <Wallet size={12} /> Bolsa de horas
        </div>
        <div className="text-sm text-ink-muted">
          No aplica para este trabajador — no tiene activado "Acepta horas extra" ni permite fichar antes de la hora de entrada.
        </div>
      </div>
    );
  }

  // Cómputo compartido (src/hooks/useWorkerHourBalance.js): el MISMO que usa
  // el chip de Equipo, así la ficha y la lista no pueden dar números distintos.
  // Días en la zona del trabajador (Canarias / península).
  const totals = computeBalanceTotals(events, workhours, holidaysSet, manualOffsets, timeZoneForWorker(worker));
  // Filas marcadoras de inicio de cómputo: fuera de la lista de ajustes (no
  // son ajustes) y fuera de los botones de borrado (se gestionan desde la
  // cabecera de la ficha, que crea la nueva antes de borrar la vieja).
  const adjustments = manualOffsets.filter((o) => !isComputationStartOffset(o));
  const computationStart = totals.computationStart;
  const computationStartRow = computationStart ? (
    <div className="mt-4 flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs">
      <CalendarCheck size={13} className="text-ink-muted shrink-0 mt-0.5" />
      <div className="min-w-0">
        <div className="text-ink font-semibold">{COMPUTATION_START_LABEL}: <span className="tabular-nums">{fmtDate(computationStart)}</span></div>
        <div className="text-ink-muted mt-0.5">
          Las horas cuentan desde esa fecha. Lo anterior queda fuera de la bolsa (ni jornada esperada, ni faltas).
        </div>
      </div>
    </div>
  ) : null;
  const auto = { totalMinutes: totals.auto, byDay: totals.byDay };
  const manualMin = totals.manual;
  const totalMin = totals.total;
  // Sin horario asignado no hay número (decisión de producto): antes cada
  // día contaba como descanso y salían cientos de horas extra falsas.
  const noSchedule = totals.noSchedule;
  const colorKey = noSchedule ? 'neutral' : balanceColor(totalMin);
  const accent = {
    positive: { wrap: 'border-success/30 bg-success/5', big: 'text-success', dot: 'bg-success' },
    negative: { wrap: 'border-danger/30 bg-danger/5',   big: 'text-danger',  dot: 'bg-danger' },
    neutral:  { wrap: 'border-slate-200',              big: 'text-ink',     dot: 'bg-slate-400' },
  }[colorKey];

  return (
    <>
      <div className={`bg-white border rounded-xl ${accent.wrap}`}>
        <div className="px-4 py-3 border-b border-slate-200/60 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-ink-muted font-semibold">
            <Wallet size={12} /> Bolsa de horas
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={reload}
              disabled={loading}
              className="text-ink-muted hover:text-ink p-1 rounded transition"
              title="Recargar"
            >
              <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
            </button>
            {canManage && (
              <button
                type="button"
                onClick={() => setModalOpen(true)}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold text-primary border border-primary/30 hover:bg-primary-soft transition"
              >
                <Plus size={12} /> Ajuste manual
              </button>
            )}
          </div>
        </div>

        <div className="px-4 py-4">
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-4 text-ink-muted">
              <Loader2 size={14} className="animate-spin" />
              <span className="text-sm">Cargando bolsa…</span>
            </div>
          ) : (error || eventsError) ? (
            <div className="text-sm text-danger flex items-start gap-2">
              <AlertTriangle size={14} className="shrink-0 mt-0.5" />
              <span>No se pudo cargar la bolsa: {(error || eventsError)?.message || 'error desconocido'}</span>
            </div>
          ) : noSchedule ? (
            <>
              <div className="font-display text-lg font-bold text-ink-muted">
                {formatNoScheduleBalance(manualMin)}
              </div>
              <div className="text-xs text-ink-muted mt-1">
                No se puede calcular la bolsa sin una jornada configurada.
                {canManage && <> {NO_SCHEDULE_ADMIN_HINT}.</>}
              </div>

              {computationStartRow}

              {adjustments.length > 0 && (
                <div className="mt-4">
                  <div className="text-xs uppercase tracking-wider text-ink-muted font-semibold mb-2">
                    Ajustes manuales
                  </div>
                  <ul className="border border-slate-200 rounded-lg divide-y divide-slate-100 bg-white max-h-64 overflow-y-auto">
                    {[...adjustments].sort((a, b) => new Date(b.date) - new Date(a.date)).map((o) => {
                      const minutes = Math.round(Number(o.offset || 0) * 60);
                      // Offset antiguo de ausencia por horas: ya no cuenta (la bolsa la
                      // recupera en el cómputo automático). Se lista tachado, "sin efecto".
                      const superseded = isSupersededHourlyOffset(o);
                      const colorClass = superseded ? 'text-ink-muted line-through' : minutes > 0 ? 'text-success' : minutes < 0 ? 'text-danger' : 'text-ink';
                      return (
                        <li key={o.id} className="px-3 py-2 flex items-start gap-3 text-sm">
                          <div className={`font-bold tabular-nums shrink-0 w-20 ${colorClass}`}>
                            {fmtMinutes(minutes)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-xs text-ink-muted tabular-nums">{fmtDate(o.date)}</div>
                            <div className="text-sm text-ink truncate">{o.reason || <span className="italic text-ink-muted">(sin motivo)</span>}</div>
                            {superseded && (
                              <div className="text-[10px] font-bold uppercase tracking-wider text-ink-muted" title={SUPERSEDED_OFFSET_TOOLTIP}>Sin efecto</div>
                            )}
                          </div>
                          {canManage && (
                            <button
                              type="button"
                              onClick={() => handleDelete(o.id)}
                              disabled={deletingId === o.id}
                              className="text-danger/70 hover:text-danger p-1 rounded transition shrink-0"
                              title="Eliminar este ajuste"
                            >
                              {deletingId === o.id ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                            </button>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </>
          ) : (
            <>
              <div className={`font-display text-3xl font-bold tabular-nums ${accent.big}`}>
                {formatBalance(totalMin)}
              </div>
              <div className="text-xs text-ink-muted mt-1">
                {colorKey === 'positive' && 'La empresa le debe estas horas al trabajador.'}
                {colorKey === 'negative' && 'El trabajador le debe estas horas a la empresa.'}
                {colorKey === 'neutral' && 'Saldo equilibrado — al día.'}
              </div>

              <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
                <div className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                  <div className="text-ink-muted font-semibold uppercase tracking-wider">Automático</div>
                  <div className="text-base font-bold text-ink tabular-nums mt-0.5">{fmtMinutes(auto.totalMinutes)}</div>
                  <div className="text-[10px] text-ink-muted mt-0.5">Calculado de fichajes vs. horario</div>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                  <div className="text-ink-muted font-semibold uppercase tracking-wider">Ajustes manuales</div>
                  <div className="text-base font-bold text-ink tabular-nums mt-0.5">{fmtMinutes(manualMin)}</div>
                  <div className="text-[10px] text-ink-muted mt-0.5">{totals.manualCount} {totals.manualCount === 1 ? 'entrada' : 'entradas'}</div>
                </div>
              </div>

              {computationStartRow}

              {/* Lista de ajustes manuales */}
              {adjustments.length > 0 && (
                <div className="mt-4">
                  <div className="text-xs uppercase tracking-wider text-ink-muted font-semibold mb-2">
                    Ajustes manuales
                  </div>
                  <ul className="border border-slate-200 rounded-lg divide-y divide-slate-100 bg-white max-h-64 overflow-y-auto">
                    {[...adjustments].sort((a, b) => new Date(b.date) - new Date(a.date)).map((o) => {
                      const minutes = Math.round(Number(o.offset || 0) * 60);
                      // Offset antiguo de ausencia por horas: ya no cuenta (la bolsa la
                      // recupera en el cómputo automático). Se lista tachado, "sin efecto".
                      const superseded = isSupersededHourlyOffset(o);
                      const colorClass = superseded ? 'text-ink-muted line-through' : minutes > 0 ? 'text-success' : minutes < 0 ? 'text-danger' : 'text-ink';
                      return (
                        <li key={o.id} className="px-3 py-2 flex items-start gap-3 text-sm">
                          <div className={`font-bold tabular-nums shrink-0 w-20 ${colorClass}`}>
                            {fmtMinutes(minutes)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-xs text-ink-muted tabular-nums">{fmtDate(o.date)}</div>
                            <div className="text-sm text-ink truncate">{o.reason || <span className="italic text-ink-muted">(sin motivo)</span>}</div>
                            {superseded && (
                              <div className="text-[10px] font-bold uppercase tracking-wider text-ink-muted" title={SUPERSEDED_OFFSET_TOOLTIP}>Sin efecto</div>
                            )}
                          </div>
                          {canManage && (
                            <button
                              type="button"
                              onClick={() => handleDelete(o.id)}
                              disabled={deletingId === o.id}
                              className="text-danger/70 hover:text-danger p-1 rounded transition shrink-0"
                              title="Eliminar este ajuste"
                            >
                              {deletingId === o.id ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                            </button>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              {/* Breakdown del cálculo automático (collapsible) */}
              {auto.byDay.length > 0 && (
                <div className="mt-4">
                  <button
                    type="button"
                    onClick={() => setShowAutoBreakdown((v) => !v)}
                    className="text-xs font-semibold text-primary hover:text-primary-darker transition"
                  >
                    {showAutoBreakdown ? '▼' : '▶'} Detalle del cómputo automático ({auto.byDay.length} días con fichaje)
                  </button>
                  {showAutoBreakdown && (
                    <div className="mt-2 border border-slate-200 rounded-lg max-h-72 overflow-y-auto">
                      <table className="w-full text-xs">
                        <thead className="bg-slate-50 sticky top-0">
                          <tr className="text-left text-ink-muted">
                            <th className="px-2 py-1.5 font-semibold">Día</th>
                            <th className="px-2 py-1.5 font-semibold text-right">Esperado</th>
                            <th className="px-2 py-1.5 font-semibold text-right">Trabajado</th>
                            <th className="px-2 py-1.5 font-semibold text-right">Δ</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {auto.byDay.map((d) => (
                            <tr key={d.date}>
                              <td className="px-2 py-1.5 tabular-nums">{fmtDate(d.date)}</td>
                              <td className="px-2 py-1.5 text-right tabular-nums text-ink-muted">{fmtMinutes(d.expected)}</td>
                              <td className="px-2 py-1.5 text-right tabular-nums">{fmtMinutes(d.worked)}</td>
                              <td className={`px-2 py-1.5 text-right tabular-nums font-semibold ${
                                d.delta > 0 ? 'text-success' : d.delta < 0 ? 'text-danger' : 'text-ink'
                              }`}>{fmtMinutes(d.delta)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <HourBalanceModal
        open={modalOpen}
        worker={worker}
        onClose={() => setModalOpen(false)}
        onSubmitted={() => reload()}
      />
    </>
  );
};

export default HourBalanceCard;
